/**
 * Annotation object model (milestone 4). Shared by the client engine, the drawing UI and tests.
 * The server stores `data` as an opaque JSON string (≤ 32 KB) and never deep-parses it; the
 * client validates everything it receives with these schemas (shared layers come from other users).
 *
 * Coordinates are QUANTISED integers 0..Q of the page's width (x) and height (y), so they stay
 * aligned at any zoom/tier and keep rows small. Sizes/widths are integers in 1/Q of page WIDTH.
 * Ink pressure is 0..1000.
 */
import { z } from 'zod';
import { isSmuflGlyphName } from './smufl';

export const Q = 10_000;
export const ANNOTATION_KINDS = ['highlight', 'ink', 'text', 'stamp', 'connector'] as const;
export type AnnotationKind = (typeof ANNOTATION_KINDS)[number];

/** Pen / text / stamp / connector colours (ChoirMate palette: black, blue, red, green, orange). */
export const PEN_COLORS = ['#111111', '#1f4fd1', '#d32f2f', '#2e7d32', '#ef8a17'] as const;
/** White: the pen's cover-up ink (whites out marks on the paper, to write over them later). */
export const WHITE_INK = '#ffffff';
/** The pen's colours: PEN_COLORS plus white (pen strokes only: white text / symbols / pins would vanish on the paper). */
export const INK_COLORS = [...PEN_COLORS, WHITE_INK] as const;
/** Highlighter colours (drawn with multiply blend). */
export const HIGHLIGHT_COLORS = ['#ffe14d', '#7ee081', '#ff8fc8', '#7cc7ff'] as const;

/** Width presets in 1/Q of page width. */
export const PEN_WIDTHS = [20, 40, 70] as const; // ≈ 0.002, 0.004, 0.007 of page width
/** Highlighter widths: thin line, about half a staff, about a full 5-line staff (ChoirMate-like). */
export const HIGHLIGHT_WIDTHS = [120, 280, 480] as const;
export const TEXT_SIZES = [180, 250, 350] as const;
/** Allowed text size (1/Q of page width). */
export const TEXT_SIZE_LIMITS = [80, 1200] as const;
export const STAMP_SIZE_RANGE = [200, 800] as const; // em size in 1/Q of page width
/**
 * Connector pin: the circle's diameter in 1/Q of page width, so a pin covers the same notes at any
 * zoom and on any device. Optional in the data (older connectors have none): absent = the default.
 */
export const CONNECTOR_SIZE_RANGE = [100, 600] as const;
export const DEFAULT_CONNECTOR_SIZE = 180; // ≈ 21 px on a fit-width landscape iPad page; two-up: the 16 px minimum (viewer/pinSize.ts)
export const MAX_INK_POINTS = 2000;
export const MAX_TEXT_CHARS = 500;
export const MAX_LABEL_CHARS = 40;
export const MAX_LIVE_OBJECTS_PER_LAYER = 5000;
export const MAX_DATA_BYTES = 32_768;

const zColor = z.string().regex(/^#[0-9a-f]{6}$/i);
const zCoord = z.number().int().min(0).max(Q);
const zPt = z.tuple([zCoord, zCoord]);
const zWidth = z.number().int().min(1).max(2000);

export const zHighlight = z
  .object({
    a: zPt,
    b: zPt,
    color: zColor,
    width: zWidth,
  })
  .strict();

/** Flat array of (x, y, pressure) triplets. */
export const zInk = z
  .object({
    pts: z
      .array(z.number().int().min(0).max(Q))
      .min(3)
      .max(MAX_INK_POINTS * 3)
      .refine((a) => a.length % 3 === 0, 'pts must be x,y,p triplets'),
    color: zColor,
    width: zWidth,
  })
  .strict();

export const zText = z
  .object({
    at: zPt,
    text: z.string().min(1).max(MAX_TEXT_CHARS),
    color: zColor,
    size: z.number().int().min(TEXT_SIZE_LIMITS[0]).max(TEXT_SIZE_LIMITS[1]),
    bold: z.boolean().optional(),
  })
  .strict();

export const zStamp = z
  .object({
    at: zPt,
    glyph: z.string().refine(isSmuflGlyphName, 'unknown glyph'),
    color: zColor,
    size: z.number().int().min(STAMP_SIZE_RANGE[0]).max(STAMP_SIZE_RANGE[1]),
    rot: z.number().int().min(-180).max(180).optional(),
  })
  .strict();

export const zConnector = z
  .object({
    at: zPt,
    target: z.object({ page: z.number().int().min(0).max(499), at: zPt }).strict(),
    label: z.string().max(MAX_LABEL_CHARS).optional(),
    color: zColor,
    size: z.number().int().min(CONNECTOR_SIZE_RANGE[0]).max(CONNECTOR_SIZE_RANGE[1]).optional(),
  })
  .strict();

export type HighlightData = z.infer<typeof zHighlight>;
export type InkData = z.infer<typeof zInk>;
export type TextData = z.infer<typeof zText>;
export type StampData = z.infer<typeof zStamp>;
export type ConnectorData = z.infer<typeof zConnector>;

export interface DataByKind {
  highlight: HighlightData;
  ink: InkData;
  text: TextData;
  stamp: StampData;
  connector: ConnectorData;
}

const DATA_SCHEMAS = {
  highlight: zHighlight,
  ink: zInk,
  text: zText,
  stamp: zStamp,
  connector: zConnector,
} as const;

interface ObjBase {
  id: string;
  layerId: string;
  /** 0-based page index. */
  page: number;
  /** Hybrid logical clock: ms; ties broken by deviceId. */
  hlcMs: number;
  deviceId: string;
  /** Tombstone (ms) — deleted objects are kept until purged so deletes sync. */
  deletedAt: number | null;
}

export type AnnotationObject = {
  [K in AnnotationKind]: ObjBase & { kind: K; data: DataByKind[K] };
}[AnnotationKind];

export type AnnotationOf<K extends AnnotationKind> = Extract<AnnotationObject, { kind: K }>;

/** Validate a decoded payload for a kind. Returns null when invalid (drop it, never crash). */
export function parseData<K extends AnnotationKind>(kind: K, data: unknown): DataByKind[K] | null {
  const r = DATA_SCHEMAS[kind].safeParse(data);
  return r.success ? (r.data as DataByKind[K]) : null;
}

/** Wire row (dto/annotations.ts AnnotationRow) → validated object, or null when invalid. */
export function rowToObject(row: {
  id: string;
  layerId: string;
  page: number;
  kind: string;
  data: string;
  hlcMs: number;
  deviceId: string;
  deletedAt: number | null;
}): AnnotationObject | null {
  if (!(ANNOTATION_KINDS as readonly string[]).includes(row.kind)) return null;
  const kind = row.kind as AnnotationKind;
  if (row.deletedAt !== null) {
    // Tombstones may carry an empty payload.
    return { id: row.id, layerId: row.layerId, page: row.page, kind, data: {} as never, hlcMs: row.hlcMs, deviceId: row.deviceId, deletedAt: row.deletedAt } as AnnotationObject;
  }
  let decoded: unknown;
  try {
    decoded = JSON.parse(row.data);
  } catch {
    return null;
  }
  const data = parseData(kind, decoded);
  if (!data) return null;
  return { id: row.id, layerId: row.layerId, page: row.page, kind, data, hlcMs: row.hlcMs, deviceId: row.deviceId, deletedAt: null } as AnnotationObject;
}

export function objectToRow(o: AnnotationObject) {
  return {
    id: o.id,
    layerId: o.layerId,
    page: o.page,
    kind: o.kind,
    data: o.deletedAt !== null ? '{}' : JSON.stringify(o.data),
    hlcMs: o.hlcMs,
    deviceId: o.deviceId,
    deletedAt: o.deletedAt,
  };
}

/** Total order for last-write-wins: (hlcMs, deviceId). */
export function hlcCompare(a: { hlcMs: number; deviceId: string }, b: { hlcMs: number; deviceId: string }): number {
  if (a.hlcMs !== b.hlcMs) return a.hlcMs < b.hlcMs ? -1 : 1;
  return a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : 0;
}

/** Normalised float (0..1) → quantised int. */
export const quant = (v: number): number => Math.max(0, Math.min(Q, Math.round(v * Q)));
/** Quantised int → normalised float (0..1). */
export const unquant = (v: number): number => v / Q;

/**
 * Ramer–Douglas–Peucker simplification on flat (x, y, p) triplets (quantised units).
 * epsilon in quantised units (≈ 5 = 0.0005 of the page).
 */
export function simplifyInk(pts: readonly number[], epsilon = 5): number[] {
  const n = pts.length / 3;
  if (n <= 2) return [...pts];
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    const x1 = pts[s * 3]!, y1 = pts[s * 3 + 1]!, x2 = pts[e * 3]!, y2 = pts[e * 3 + 1]!;
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    let maxD = -1, idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = Math.abs(dy * pts[i * 3]! - dx * pts[i * 3 + 1]! + x2 * y1 - y2 * x1) / len;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > epsilon && idx > 0) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i * 3]!, pts[i * 3 + 1]!, pts[i * 3 + 2]!);
  // Hard cap: evenly decimate if still too long.
  if (out.length / 3 > MAX_INK_POINTS) {
    const m = out.length / 3;
    const step = m / MAX_INK_POINTS;
    const dec: number[] = [];
    for (let k = 0; k < MAX_INK_POINTS; k++) {
      const i = Math.min(m - 1, Math.round(k * step));
      dec.push(out[i * 3]!, out[i * 3 + 1]!, out[i * 3 + 2]!);
    }
    return dec;
  }
  return out;
}
